/* eslint-disable no-param-reassign */
import { useBeforeUnload, useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Box, Checkbox, SxProps, TableRow, Typography, TextField, Tooltip, SpeedDial } from "@mui/material";
import { SuccessAlertContext } from "App";
import HelpIcon from "@mui/icons-material/Help";
import dayjs from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { checkAccessTokenExpired } from "util/auth";
import ERROR_MESSAGE from "constants/message";
import Notify from "config/notify";
import { SLASH_FORMAT_DATE } from "constants/format";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  AmountSymbol,
  PrefProduct,
  StorehouseSupplierOptions,
  SupplierSearchApiParams,
  SupplierStock,
  SupplierStockSet,
  SupplierStockSingle,
  SupplierStockSingleInSet,
} from "../types";
import useStockSearch from "../hooks/useStockSearch";
import StockSearchDialog from "../components/StockSearchDialog";
import StockTable from "../components/StockTable";
import StockContainer from "../components/StockContainer";
import {
  amountSymbols,
  amountSymbolsWithBlank,
  INT_MAX,
  stockVisibleTypeLabel,
  storehouseSupplierOptions,
} from "../constants";
import TableCellSmall, {
  MultiTypography,
  TableCellNarrow,
  TableCellWide,
  TableHeaderCellNarrow,
  TableHeaderCellSmall,
  TableHeaderCellWide,
} from "../components/TableCellSmall";
import StockUpdateConfirmDialog from "../components/StockUpdateConfirmDialog";
import useSupplierStockList from "../hooks/useSupplierStockList";
import {
  calcSymbol,
  checkSymbolThresholdExist,
  dispSymbol,
  extractNoDupplicationSupplier,
  removeEmptyValues,
} from "../utils/common";
import CsvModals from "../components/CsvModals";
import updateStockApi from "../api/updateStockApi";
import CustomTooltip from "../components/CustomTooltip";
import { SelectForm } from "../components/Common";
import PrefDialog from "../components/PrefDialog";

const limit = 10;

const styles: { [key: string]: SxProps } = {
  supplierTopCol: {
    textAlign: "center",
    p: 0.5,
    borderBottom: "none",
  },
  supplierBottomCol: {
    textAlign: "center",
    p: 0.5,
    top: 58,
  },
};

/**
 * 商品管理部在庫一覧更新画面
 * @returns JSX.Element
 */
export default function ProductStock() {
  const [searchParams] = useSearchParams();
  const { showAlert } = useContext(SuccessAlertContext);
  const [isLoading, setIsLoading] = useState(false);
  // 取得するデータ自体はSupplierStockList.tsxと同じため、同じhooksを使ってデータを取得する。
  const [
    {
      total,
      stockList,
      isLoading: isStockLoading,
      isMoreLoading: isMoreStockLoading,
      changedStockList,
      isFastLoading,
      fireMoreLoadEvent,
      isPrefLoading,
      prefStockList,
    },
    {
      setStockList,
      get: getStockList,
      getMore: getMoreStockList,
      updatedStockList,
      setFireMoreLoadEvent,
      setInit,
      setChangedStockList,
      getPrefStock,
    },
  ] = useSupplierStockList(showAlert);
  const [isOpenStockSearch, setIsOpenStockSearch] = useState(true);
  const [isOpenUpdateConfirmDialog, setIsOpenUpdateConfirmDialog] = useState(false);
  // メニュー選択からsupplierNameは取得する。
  const [supplierName, setSupplierName] = useState("");
  const [isReadonly, setIsReadonly] = useState(false);
  const [isOpenStockCsvUpload, setIsOpenStockCsvUpload] = useState(false);
  const [isOpenThresholdCsvUpload, setIsOpenThresholdCsvUpload] = useState(false);
  const [storehouseSupplier, setStorehouseSupplier] = useState<StorehouseSupplierOptions>(
    searchParams.get("isStorehouse") === "true" ? "倉庫" : "仕入先",
  );
  const [title, setTitle] = useState(searchParams.get("searchName") ?? "");

  const defaultIsStorehouse = searchParams.get("isStorehouse") === "true";
  const defaultStorehouse = defaultIsStorehouse ? searchParams.get("searchName") : "";
  const defaultSupplier = !defaultIsStorehouse ? searchParams.get("searchName") : "";
  const defaultSupplierStorehouseCd = searchParams.get("code");
  const [isOpenStockPref, setIsOpenStockPref] = useState(false);

  // 仕入先複数在庫表示対応
  const [selectedRegionWarehouseIndex, setSelectedRegionWarehouseIndex] = useState<number>(0); // 地域倉庫のindex(supplierWarehouseのindexではなく!isCenterでfilterされたindex)
  const [selectedCenterWarehouseIndex, setSelectedCenterWarehouseIndex] = useState<number>(0); // センターのindex(supplierWarehouseのindexではなくisCenterでfilterされたindex)
  const [selectedPrefProduct, setSelectedPrefProduct] = useState<PrefProduct | undefined>(undefined); // 閲覧モードで商品コードをクリックした場合

  const [
    {
      productCd,
      productName,
      brand,
      JANCd,
      productNumber,
      tyreWidth,
      tyreRatio,
      tyreRimDiam,
      wheelWidth,
      wheelPcd,
      wheelHole,
      wheelRimDiam,
      classification,
      storehouseSupplierWithBlank,
      storehouse,
      supplier,
    },
    {
      setProductCd,
      setProductName,
      setBrand,
      setJANCd,
      setProductNumber,
      setTyreWidth,
      setTyreRatio,
      setTyreRimDiam,
      setWheelWidth,
      setWheelPcd,
      setWheelHole,
      setWheelRimDiam,
      setClassification,
      setStorehouseSupplierWithBlank,
      setStorehouse,
      setSupplier,
      setCategory,
    },
    { classificationOptions, tyreOptions, wheelOptions, storehouseOptions, brandOptions },
    { isLoading: isOptionsLoading },
  ] = useStockSearch(true, defaultStorehouse, defaultSupplier);

  const [isUpdateProcessing, setIsUpdateProcessing] = useState(false);
  const csvDownloading = useRef(false);
  const navigate = useNavigate();

  const [isScrollTop, setIsScrollTop] = useState(false);
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handlePrefStockClose = () => {
    setSelectedPrefProduct(undefined);
    setIsOpenStockPref(false);
  }

  // 仕入先かどうかをチェック
  const checkIsSupplier = useCallback(
    () => storehouseSupplier === storehouseSupplierOptions[1],
    [storehouseSupplier],
  );

  // 現在の検索条件にあるデータを基にAPIのパラメータを作成
  const createGetListApiParams = (
    ss?: StorehouseSupplierOptions,
    isInit?: boolean,
  ): SupplierSearchApiParams => {
    let code = "";
    if (
      ss ? ss === storehouseSupplierOptions[1] : storehouseSupplier === storehouseSupplierOptions[1]
    ) {
      // 仕入先で検索する場合
      code = extractNoDupplicationSupplier(classificationOptions).some(
        (so) => so.supplierName === supplier,
      )
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "";
      console.log("仕入先検索:", code, extractNoDupplicationSupplier(classificationOptions), extractNoDupplicationSupplier(classificationOptions).some(
        (so) => so.supplierName === supplier,
      ));
    } else if (
      ss ? ss === storehouseSupplierOptions[0] : storehouseSupplier === storehouseSupplierOptions[0]
    ) {
      // 外部倉庫で検索する場合
      code = storehouseOptions.some((so) => so.name === storehouse)
        ? storehouseOptions.filter((so) => so.name === storehouse)[0].id
        : "";
      console.log("外部倉庫検索:", code);
    }
    const params: SupplierSearchApiParams = {
      code: isInit ? (defaultSupplierStorehouseCd ?? "") : code,
      item_cd: productCd,
      item_name: productName,
      brand: brand ?? "",
      jan_cd: JANCd,
      item_num: productNumber,
      tire_width: classification === "タイヤ" ? tyreWidth : undefined,
      tire_ratio: classification === "タイヤ" ? tyreRatio : undefined,
      tire_rim_size: classification === "タイヤ" ? tyreRimDiam : undefined,
      wheel_width: classification === "ホイール" ? wheelWidth : undefined,
      wheel_pcd: classification === "ホイール" ? wheelPcd : undefined,
      wheel_hole: classification === "ホイール" ? wheelHole : undefined,
      wheel_rim_size: classification === "ホイール" ? wheelRimDiam : undefined,
      class_cd: classificationOptions.some((co) => co.name === classification)
        ? classificationOptions.filter((co) => co.name === classification)[0].id
        : "",
    };
    return removeEmptyValues(params);
  };

  // 検索するときに仕入先かどうかを判定する。
  const checkIsSupplierParam = useCallback(
    () => storehouseSupplierWithBlank ? storehouseSupplierWithBlank === storehouseSupplierOptions[1] : storehouseSupplier === storehouseSupplierOptions[1],
    [storehouseSupplierWithBlank, storehouseSupplier],
  );

  const getSupplierParam = useCallback(() => {
    const supCd = extractNoDupplicationSupplier(classificationOptions).some(
        (so) => so.supplierName === supplier,
      )
        ? extractNoDupplicationSupplier(classificationOptions).filter(
            (so) => so.supplierName === supplier,
          )[0].supplierCd
        : "";
    return supCd;
  }, [classificationOptions, supplier, extractNoDupplicationSupplier]);

  // 2回目以降getStockListを呼び出す場合、更新状態をリセットする。
  const getStockListUpdate = async (ss?: StorehouseSupplierOptions) => {
    setChangedStockList([]);
    setSelectedRegionWarehouseIndex(0);
    setSelectedCenterWarehouseIndex(0);
    // 仕入先の場合は仕入先コードをfillするために取得
    const isSup = checkIsSupplierParam();
    if (isSup) {
      const supCd = getSupplierParam();
      await getStockList(createGetListApiParams(ss), isSup, supCd);
    } else {
      await getStockList(createGetListApiParams(ss), isSup);
    }
  };

  const onSearchClick = async () => {
    if ((storehouse === "" && supplier === "") || storehouseSupplierWithBlank === "") {
      showAlert("仕入先名または倉庫名を入力してください。", "error");
      return;
    }
    setTitle(supplierName);
    setStorehouseSupplier(storehouseSupplierWithBlank);
    setIsOpenStockSearch(false);
    // stateが変更されていることを保証するため、createGetListApiParamsに引数を入れる
    await getStockListUpdate(storehouseSupplierWithBlank);
  };

  // 単品かセットか
  const checkProductSet = useCallback(
    (stock: SupplierStock): stock is SupplierStockSet => "productSetCd" in stock,
    [],
  );

  // 単品であることは前提で、プルダウンを押したときに表示される、セットに付随する単品かどうか
  const checkProductSingleInSet = useCallback(
    (stock: SupplierStockSingle | SupplierStockSingleInSet): stock is SupplierStockSingleInSet =>
      "quantity" in stock,
    [],
  );

  const handleToggleOpenRow = useCallback(
    (stockSetId: string, flag: boolean) => {
      setStockList(() =>
        stockList.map((s) => {
          if (checkProductSet(s) && s.id === stockSetId) {
            s.show = flag;
          }
          return s;
        }),
      );
    },
    [checkProductSet, stockList],
  );

  const dispSymbolReadonly = (stock: SupplierStockSingle) => stock.amountCount; // 外部倉庫は常に数値表示
    // dispSymbol(stock.amountCount, stock.stockVisibleType, stock.symbolThresholdList);

  const onChangeAmount = useCallback(
    (value: string, index: number) => {
      let newValue: number | AmountSymbol | undefined;
      const newStockList = [...stockList];
      if (value === amountSymbolsWithBlank[0]) {
        newValue = undefined;
        newStockList[index].amountSymbol = newValue;
        if (newStockList[index].amountCount === undefined) {
          newStockList[index].amountCount = 0;
        }
      } else if (typeof Number(value) === "number" && !Number.isNaN(Number(value))) {
        newValue = Number(value);
        if (newValue > 10 ** 20) {
          return;
        }
        if (
          storehouseSupplier === storehouseSupplierOptions[1] &&
          stockList[index].symbolThresholdList
        ) {
          newStockList[index].amountSymbol = calcSymbol(
            newValue,
            stockList[index].symbolThresholdList!,
          );
        }
        newStockList[index].amountCount = newValue;
      } else if (
        typeof value === "string" &&
        (amountSymbols as readonly string[]).includes(value)
      ) {
        newValue = value as AmountSymbol;
        newStockList[index].amountSymbol = newValue;
      } else {
        return;
      }
      setStockList(newStockList);
      updatedStockList();
    },
    [stockList, storehouseSupplier, storehouseSupplierOptions, updatedStockList],
  );

  // 更新確認画面を開く
  const onUpdateConfirm = () => {
    setIsOpenUpdateConfirmDialog(true);
    updatedStockList();
  };

  // 確認ダイアログで更新ボタンを押下した時
  const onUpdateAmount = async () => {
    const sList = updatedStockList();
    setIsUpdateProcessing(true);
    let successFlag = true;
    try {
      const isSupplier = checkIsSupplier();
      const noDupplicateSupplier = extractNoDupplicationSupplier(classificationOptions);
      let supplierCd;
      if (storehouseSupplier === storehouseSupplierOptions[0]) {
        supplierCd = storehouseOptions.some((so) => so.name === supplierName)
          ? storehouseOptions.filter((so) => so.name === supplierName)[0].id
          : "";
      } else {
        supplierCd = noDupplicateSupplier.some((so) => so.supplierName === supplierName)
          ? noDupplicateSupplier.filter((so) => so.supplierName === supplierName)[0].supplierCd
          : "";
      }
      // ToDo: 更新の引数を決める必要あり
      await updateStockApi({ stockList: sList, isSupplier, supplierCd });
      showAlert("在庫数量を更新しました。");
      setIsOpenUpdateConfirmDialog(false);
    } catch (err) {
      console.error(err);
      successFlag = false;
      showAlert("更新に失敗しました。", "error");
      setIsOpenUpdateConfirmDialog(false);
    } finally {
      setIsUpdateProcessing(false);
    }

    // 取得し直し
    if (successFlag) {
      await getStockListUpdate();
    }
  };

  const onChangeReadonlyScreen = () => {
    setIsReadonly((prev) => !prev);
  };

  const handleBack = () => navigate(-1);

  // 更新しないままサイトを離れた時に警告する
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        if (changedStockList.length && !csvDownloading.current) e.preventDefault();
      },
      [changedStockList],
    ),
    { capture: true },
  );

  // 地域倉庫があるかどうか
  const checkExistRegionWarehouse = () => !!(stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0);

  // センター倉庫があるかどうか
  const checkExistCenterWarehouse = () => !!(stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0);

  const onChangeArrival = useCallback(
    (s: string, index: number) => {
      const newStockList = [...stockList];
      newStockList[index].arrivalDate = s;
      setStockList(newStockList);
      updatedStockList();
    },
    [stockList, updatedStockList],
  );

  // 仕入先センター、地域倉庫数量変更
  const onChangeWarehouseAmount = useCallback((value: string, index: number, warehouseIndex: number) => {
    setStockList((prev) => {
      const newStockList = [...prev];
      const w = newStockList[index].supplierWarehouses;
      if(w) w[warehouseIndex].amount = Number(value);
      return newStockList;
    });
    updatedStockList();
  }, [updatedStockList]);

  const editableWarehouseAmountCell = useCallback((sw: NonNullable<SupplierStockSingle["supplierWarehouses"]>[number], index: number, warehouseIndex: number) => (
    <TextField type="Number" size="small" value={sw.amount} onChange={
        (e) => {
          if (Number(e.target.value) > INT_MAX) {
            return;
          }
          onChangeWarehouseAmount(e.target.value, index, warehouseIndex)
        }
      }
      inputProps={{
        sx: {fontSize: 12, textAlign: 'right'},
        min: 0,
        max: INT_MAX
      }}
      onKeyDown={(e) => {if (e.key === '-' || e.key === '.') e.preventDefault()}}
    />
  ), [onChangeWarehouseAmount]);

  // 外部倉庫数量変更セル
  const editableAmountCountCell = useCallback((stock: SupplierStockSingle, index: number) => (
    <TextField type="Number" size="small" value={stock.amountCount} onChange={
        (e) => {
          if (Number(e.target.value) > INT_MAX) {
            return;
          }
          onChangeAmount(e.target.value, index)
        }
      }
      inputProps={{
        sx: {fontSize: 12, textAlign: 'right'},
        min: 0,
        max: INT_MAX
      }}
      onKeyDown={(e) => {if (e.key === '-' || e.key === '.') e.preventDefault()}}
    />
  ), [onChangeAmount]);
  const editableArrivalDateCell = useCallback(
    (stock: SupplierStockSingle, index: number) => (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <DatePicker
          // label="入荷予定日"
          format={SLASH_FORMAT_DATE}
          value={stock.arrivalDate ? dayjs(stock.arrivalDate, SLASH_FORMAT_DATE) : null}
          onChange={(val) => onChangeArrival(val ? val.format(SLASH_FORMAT_DATE) : "", index)}
          slotProps={{ textField: { size: "small" } }}
          sx={{
            "& .MuiInputBase-input.MuiOutlinedInput-input": { fontSize: 12, minWidth: 90 },
          }}
        />
      </LocalizationProvider>
    ),
    [onChangeArrival],
  );

  const readOnlyTBodyRender = useCallback(
    (stock: SupplierStockSingle | SupplierStockSingleInSet) => (
      <>
        {/* 仕入先のセンター倉庫ユーザー表示 */}
        { checkIsSupplier() && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0 &&
          <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}} onClick={() => {
            setSelectedPrefProduct({
              supplierCd: extractNoDupplicationSupplier(classificationOptions).filter(
                (so) => so.supplierName === supplier,
              )[0].supplierCd,
              productCd: stock.productCd,
              isCenter: true,
            });
            setIsOpenStockPref(true);
          }}><Typography fontSize={11} color="blue" sx={{textDecoration: 'underline', cursor: 'pointer'}}>
            {(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}
          </Typography></TableCellNarrow>
        }
        {/* 仕入先の地域倉庫ユーザー表示 */}
        { checkIsSupplier() && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0 &&
          <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}} onClick={() => {
            setSelectedPrefProduct({
              supplierCd: extractNoDupplicationSupplier(classificationOptions).filter(
                (so) => so.supplierName === supplier,
              )[0].supplierCd,
              productCd: stock.productCd,
              isCenter: false,
            });
            setIsOpenStockPref(true);
          }}><Typography fontSize={11} color="blue" sx={{textDecoration: 'underline', cursor: 'pointer'}}>
            {(dispSymbol(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}
          </Typography></TableCellNarrow>
        }
        { !checkIsSupplier() &&
          <TableCellNarrow sx={{ minWidth: 40 }}>
            <Box textAlign={typeof dispSymbolReadonly(stock) === "number" ? "right" : "center"}>
              {dispSymbolReadonly(stock).toLocaleString()}
            </Box>
          </TableCellNarrow>
        }
        <TableCellSmall>{stock.arrivalDate}</TableCellSmall>
        {checkIsSupplier() && (
          <>
            <TableCellSmall>
              <Box textAlign="right">
                {stock.symbolThresholdList &&
                  `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[0].toLocaleString() : "-"}`}
              </Box>
            </TableCellSmall>
            <TableCellSmall>
              <Box textAlign="right">
                {stock.symbolThresholdList &&
                  `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[stock.symbolThresholdList.length - 1].toLocaleString() : "-"}`}
              </Box>
            </TableCellSmall>
            <TableCellSmall>{stockVisibleTypeLabel[stock.stockVisibleType] ?? '-'}</TableCellSmall>
          </>
        )}
      </>
    ),
    [dispSymbolReadonly, checkIsSupplier, selectedCenterWarehouseIndex, selectedRegionWarehouseIndex],
  );
  const editableTBodyRender = useCallback(
    (stock: SupplierStockSingle | SupplierStockSingleInSet, index?: number) => (
      <>
        {checkIsSupplier() && (
          <>
            {/* 仕入先のセンター倉庫ユーザー表示 */}
            {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0 &&
              <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
            }
            {/* 仕入先の地域倉庫ユーザー表示 */}
            {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0 &&
              <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
            }
            {/* 仕入先のセンター倉庫在庫数量入力 */}
            {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0 && (
              <TableCellWide>
                { index === undefined
                  ?
                    stock.amountCount
                  :
                    editableWarehouseAmountCell(stock.supplierWarehouses.filter(
                      sw => sw.isCenter)[selectedCenterWarehouseIndex],
                      index,
                      stock.supplierWarehouses.reduce<number[]>((acc, cur, idx) => {
                        if (cur.isCenter) acc.push(idx); // 地域倉庫のみindexを抽出
                        return acc;
                      }, [])[selectedCenterWarehouseIndex]
                    )
                }
              </TableCellWide>
            )}
            {/* 仕入先の地域倉庫在庫数量入力 */}
            {stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0 && (
              <TableCellWide>
                { index === undefined
                  ?
                    stock.amountCount
                  :
                    editableWarehouseAmountCell(stock.supplierWarehouses.filter(
                      sw => !sw.isCenter)[selectedRegionWarehouseIndex],
                      index,
                      stock.supplierWarehouses.reduce<number[]>((acc, cur, idx) => {
                        if (!cur.isCenter) acc.push(idx); // 地域倉庫のみindexを抽出
                        return acc;
                      }, [])[selectedRegionWarehouseIndex]
                    )
                }
              </TableCellWide>
            )}
          </>
        )}
        { !checkIsSupplier() &&
          <TableCellWide>
            {index === undefined ? stock.amountCount : editableAmountCountCell(stock, index)}
          </TableCellWide>
        }
        <TableCellWide>
          {index === undefined ? stock.arrivalDate : editableArrivalDateCell(stock, index)}
        </TableCellWide>
        {checkIsSupplier() && (
          <>
            <TableCellSmall>
              <Box textAlign="right">
                {stock.symbolThresholdList &&
                  `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[0].toLocaleString() : "-"}`}
              </Box>
            </TableCellSmall>
            <TableCellSmall>
              <Box textAlign="right">
                {stock.symbolThresholdList &&
                  `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[stock.symbolThresholdList.length - 1].toLocaleString() : "-"}`}
              </Box>
            </TableCellSmall>
            {/* <TableCellSmall sx={{py: 0}}>{renderRadioGroup(stockVisibleTypeLabel.map(s => s), stock)}</TableCellSmall> */}
            <TableCellSmall>{stockVisibleTypeLabel[stock.stockVisibleType] ?? '-'}</TableCellSmall>
            {/* <TableCellSmall>
              <Checkbox
                checked={stock.isDiscontinued}
                disabled
                // onChange={(e) => handleChangeDiscontinued(e.target.checked, stock)}
              />
            </TableCellSmall> */}
          </>
        )}
      </>
    ),
    [editableAmountCountCell, editableArrivalDateCell, checkIsSupplier, selectedCenterWarehouseIndex, selectedRegionWarehouseIndex],
  );

  // 単品のセル
  const clickableSingleCell = useCallback(
    (
      stock: SupplierStockSingle | SupplierStockSingleInSet,
      show: boolean | undefined,
      index?: number,
    ) =>
      show ? (
        <TableRow key={stock.id}>
          <TableCellNarrow sx={{ textAlign: "center" }}>
            {stock.isDiscontinued ? (
              <CustomTooltip title="*がついているものは廃番です。" arrow>
                <Box sx={{ cursor: "pointer" }}>
                  <Typography component="span" sx={{ color: "var(--red-800)" }}>
                    *
                  </Typography>
                  {stock.productCd}
                </Box>
              </CustomTooltip>
            ) : (
              stock.productCd
            )}
          </TableCellNarrow>
          <TableCellSmall>{stock.JANCd}</TableCellSmall>
          <TableCellSmall>
            {classificationOptions.filter((c) => c.id === stock.classification).length > 0
              ? classificationOptions.filter((c) => c.id === stock.classification)[0].name
              : stock.classification}
          </TableCellSmall>
          <TableCellSmall>{stock.brand}</TableCellSmall>
          <TableCellWide>{stock.productName}</TableCellWide>
          <TableCellSmall>{stock.productNumber}</TableCellSmall>
          <TableCellSmall>{stock.size ?? null}</TableCellSmall>
          {isReadonly && readOnlyTBodyRender(stock)}
          {!isReadonly && editableTBodyRender(stock, index)}
        </TableRow>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      ),
    [
      checkProductSingleInSet,
      readOnlyTBodyRender,
      editableTBodyRender,
      classificationOptions,
      isReadonly,
    ],
  );

  // 混合のセル
  const fieldCell = useCallback(
    (stock: SupplierStock, index: number) => {
      let ele: JSX.Element;
      if (checkProductSet(stock)) {
        // セット
        ele = (
          <>
            <TableRow key={stock.id}>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                <Box display="flex" alignItems="center">
                  {stock.productSetCd}
                </Box>
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                <Box display="flex" alignItems="center">
                  {stock.JANCd}
                </Box>
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                {stock.classification}
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)} />
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                {}
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                <Box display="flex" alignItems="center">
                  {stock.productNumber}
                </Box>
              </TableCellSmall>
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)} />
              {isReadonly && (
                <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                  {stock.amountSymbol ?? stock.amountCount}
                </TableCellSmall>
              )}
              {!isReadonly && (
                <>
                  <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                    {stock.amountSymbol}
                  </TableCellSmall>
                  <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                    {stock.amountCount}
                  </TableCellSmall>
                </>
              )}
              <TableCellSmall onClick={() => handleToggleOpenRow(stock.id, !stock.show)}>
                {stock.arrivalDate}
              </TableCellSmall>
              <TableCellSmall>
                <Box textAlign="right">
                  {stock.symbolThresholdList &&
                    `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[0] : ""}`}
                </Box>
              </TableCellSmall>
              <TableCellSmall>
                <Box textAlign="right">
                  {stock.symbolThresholdList &&
                    `${stock.symbolThresholdList.length >= 2 ? [...stock.symbolThresholdList].sort((a, b) => b - a)[stock.symbolThresholdList.length - 1] : ""}`}
                </Box>
              </TableCellSmall>
              {/* <TableCellSmall>{renderRadioGroup(stockVisibleTypeLabel.map(s => s), stock)}</TableCellSmall> */}
            </TableRow>
            {stock.stockSingleList.map((pSet) => clickableSingleCell(pSet, stock.show))}
          </>
        );
      } else {
        // 単品
        ele = clickableSingleCell(stock, true, index);
      }
      return ele;
    },
    [checkProductSet, handleToggleOpenRow, clickableSingleCell, isReadonly],
  );

  const tableHeadElement = useCallback(
    () => (
      <>
        <TableRow>
          <TableHeaderCellNarrow rowSpan={2} sx={{ width: 30 }}>
            {checkIsSupplier() ? "YH商品コード" : "商品コード"}
          </TableHeaderCellNarrow>
          <TableHeaderCellSmall rowSpan={2}>JANコード</TableHeaderCellSmall>
          <TableHeaderCellSmall rowSpan={2}>大分類</TableHeaderCellSmall>
          <TableHeaderCellSmall rowSpan={2}>ブランド</TableHeaderCellSmall>
          <TableHeaderCellWide rowSpan={2}>商品名</TableHeaderCellWide>
          <TableHeaderCellSmall rowSpan={2}>品番</TableHeaderCellSmall>
          <TableHeaderCellSmall rowSpan={2}>サイズ</TableHeaderCellSmall>
          { checkIsSupplier() &&
            (Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse()) > 0) && <TableHeaderCellNarrow colSpan={Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse())} sx={{textAlign: "center"}}>ユーザー表示</TableHeaderCellNarrow>
          }
          { (isReadonly && !checkIsSupplier()) &&
            <TableHeaderCellNarrow rowSpan={2} sx={{textAlign: "center"}}>ユーザー表示</TableHeaderCellNarrow>
          }
          { !isReadonly && (
            checkIsSupplier() ?
              (Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse()) > 0) && <TableHeaderCellWide colSpan={Number(checkExistRegionWarehouse()) + Number(checkExistCenterWarehouse())} sx={{textAlign: "center"}}>在庫数量</TableHeaderCellWide> :
              <TableHeaderCellWide rowSpan={2}>在庫数量</TableHeaderCellWide>
            )
          }
          <TableHeaderCellWide rowSpan={2}>入荷予定日</TableHeaderCellWide>
          {checkIsSupplier() && (
            <>
              <TableHeaderCellSmall colSpan={2} rowSpan={1} sx={styles.supplierTopCol}>
                在庫数設定値
                <CustomTooltip
                  title={
                    <>
                      ○~△境界値：在庫数量が境界値以上になると、ユーザー表示が○に、未満だと△に変わります(在庫数量は△~×境界値以上である前提)。
                      <br />
                      △~×境界値：在庫数量が境界値以上になると、ユーザー表示が△に、未満だと×に変わります(在庫数量は○~△境界値未満である前提)。
                    </>
                  }
                  arrow
                >
                  <Box sx={{ cursor: "pointer" }}>
                    <Typography
                      component="span"
                      sx={{ color: "var(--red-600)", position: "absolute", top: 0, right: 12 }}
                    >
                      <HelpIcon sx={{ fontSize: 18 }} />
                    </Typography>
                  </Box>
                </CustomTooltip>
              </TableHeaderCellSmall>
              <TableHeaderCellSmall rowSpan={2}>在庫数表示</TableHeaderCellSmall>
              {/* {!isReadonly && <TableHeaderCellSmall rowSpan={2}>廃番</TableHeaderCellSmall>} */}
            </>
          )}
        </TableRow>
        {checkIsSupplier() && (
          <TableRow>
            {/* { stockList && stockList.length > 0 && stockList[0].supplierWarehouses?.map((sw) =>
              <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>{sw.name}<br/>倉庫</TableHeaderCellNarrow>
            )}
            <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>センター倉庫</TableHeaderCellNarrow> */}
            { !isReadonly ? (
              <>
                {/* {stockList && stockList.length > 0 && stockList[0].supplierWarehouses?.map((sw) => */}
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0 && (() => {
                  // センター倉庫がある場合表示
                  const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
                  return (
                    <TableHeaderCellWide sx={{...styles.supplierBottomCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
                      <SelectForm
                        id="center_warehouse"
                        label="センター倉庫"
                        optionList={supplierWarehouses.map(sw => sw.name)}
                        value={supplierWarehouses[selectedCenterWarehouseIndex].name}
                        setValue={(val: string) => setSelectedCenterWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
                        width={160}
                      />
                    </TableHeaderCellWide>
                  )
                })()}
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0 && (() => {
                  // 地域倉庫がある場合表示
                  const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
                  return (
                    <TableHeaderCellWide sx={{...styles.supplierBottomCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
                      <SelectForm
                        id="region_warehouse"
                        label="地域倉庫"
                        optionList={supplierWarehouses.map(sw => sw.name)}
                        value={supplierWarehouses[selectedRegionWarehouseIndex].name}
                        setValue={(val: string) => setSelectedRegionWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
                        width={160}
                      />
                    </TableHeaderCellWide>
                  )
                })()}
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0 && (() => (
                  <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>
                    {(() => {
                      // センター倉庫がある場合表示
                      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
                      return supplierWarehouses[selectedCenterWarehouseIndex].name;
                    })()}
                  </TableHeaderCellNarrow>
                ))()}

                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0 && (() => (
                  <TableHeaderCellNarrow sx={{...styles.supplierBottomCol, ...{textAlign: "center"}}}>
                    {(() => {
                      // 地域倉庫がある場合表示
                      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
                      return supplierWarehouses[selectedRegionWarehouseIndex].name;
                    })()}
                  </TableHeaderCellNarrow>
                ))()}
              </>
            ) : (
              // 本部用のみ閲覧モードの時クリックしたらモーダルが開くようにする
              <>
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0 && (() => {
                  // センター倉庫がある場合表示
                  const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
                  return (
                    <TableHeaderCellWide sx={{...styles.supplierBottomCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
                      <SelectForm
                        id="center_warehouse"
                        label="センター倉庫"
                        optionList={supplierWarehouses.map(sw => sw.name)}
                        value={supplierWarehouses[selectedCenterWarehouseIndex].name}
                        setValue={(val: string) => setSelectedCenterWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
                        width={160}
                      />
                    </TableHeaderCellWide>
                  )
                })()}
                { stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0 && (() => {
                  // 地域倉庫がある場合表示
                  const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
                  return (
                    <TableHeaderCellWide sx={{...styles.supplierBottomCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
                      <SelectForm
                        id="region_warehouse"
                        label="地域倉庫"
                        optionList={supplierWarehouses.map(sw => sw.name)}
                        value={supplierWarehouses[selectedRegionWarehouseIndex].name}
                        setValue={(val: string) => setSelectedRegionWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
                        width={160}
                      />
                    </TableHeaderCellWide>
                  )
                })()}
              </>
            )}
            <TableHeaderCellSmall sx={styles.supplierBottomCol}>
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  ○~△
                  <br />
                  境界値
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
            <TableHeaderCellSmall sx={styles.supplierBottomCol}>
              <MultiTypography sx={{ textAlign: "center" }}>
                <>
                  △~×
                  <br />
                  境界値
                </>
              </MultiTypography>
            </TableHeaderCellSmall>
          </TableRow>
        )}
      </>
    ),
    [checkIsSupplier, isReadonly, checkExistRegionWarehouse, checkExistCenterWarehouse, stockList, selectedCenterWarehouseIndex, selectedRegionWarehouseIndex],
  );

  const tableBodyElement = useCallback(
    () => (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>{stockList.length > 0 && stockList.map((stock, index) => fieldCell(stock, index))}</>
    ),
    [stockList, fieldCell],
  );

  const selectHeaderCenterWarehouse = () => {
    if (stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => sw.isCenter).length > 0) {
      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => sw.isCenter);
      return (
        <TableHeaderCellWide sx={{...styles.supplierTopCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
          <SelectForm
            id="center_warehouse"
            label="センター倉庫"
            optionList={supplierWarehouses.map(sw => sw.name)}
            value={supplierWarehouses[selectedCenterWarehouseIndex].name}
            setValue={(val: string) => setSelectedCenterWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
            width={160}
          />
        </TableHeaderCellWide>
      )
    }
    return <Box/>;
  };

  const selectHeaderRegionWarehouse = () => {
    if (stockList && stockList.length > 0 && stockList[0].supplierWarehouses && stockList[0].supplierWarehouses.length > 0 && stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter).length > 0) {
      // 地域倉庫がある場合表示
      const supplierWarehouses = stockList[0].supplierWarehouses.filter((sw) => !sw.isCenter);
      return (
        <TableHeaderCellWide sx={{...styles.supplierTopCol, ...{minWidth: 176, maxWidth: 176, width: 176}}} rowSpan={2}>
          <SelectForm
            id="region_warehouse"
            label="地域倉庫"
            optionList={supplierWarehouses.map(sw => sw.name)}
            value={supplierWarehouses[selectedRegionWarehouseIndex].name}
            setValue={(val: string) => setSelectedRegionWarehouseIndex(supplierWarehouses.reduce((acc, cur, index) => cur.name === val ? index : acc, -1))}
            width={160}
          />
        </TableHeaderCellWide>
      )
    }
    return <Box/>;
  };

  // 現在選択されているセンター倉庫数量
  const bodyCenterWarehouse = (id: string) => {
    const stock: SupplierStockSingle | undefined = stockList.find(s => s.id === id) as SupplierStockSingle;
    return stock && checkIsSupplier() && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => sw.isCenter).length > 0
      ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => sw.isCenter)[selectedCenterWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
      : <Box/>
  };
  // 現在選択されている地域倉庫数量
  const bodyRegionWarehouse = (id: string) => {
    const stock: SupplierStockSingle | undefined = stockList.find(s => s.id === id) as SupplierStockSingle;
    return stock && checkIsSupplier() && stock.supplierWarehouses && stock.supplierWarehouses.length > 0 && stock.supplierWarehouses.filter(sw => !sw.isCenter).length > 0
      ? <TableCellNarrow sx={{textAlign: "center", px: "8px", maxWidth: 60}}>{(dispSymbol(stock.supplierWarehouses.filter(sw => !sw.isCenter)[selectedRegionWarehouseIndex].amount, stock.stockVisibleType, stock.symbolThresholdList)).toLocaleString()}</TableCellNarrow>
      : <Box/>
  };

  // スクロール処理
  const handleScroll = async () => {
    setIsScrollTop(window.pageYOffset > 0);

    // ロード中は処理を行わない。stateはaddEventListenerで登録しているので効かず、代わりにrefを使う。
    if (isFastLoading.current) {
      return;
    }
    const scrollTop = window.scrollY;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    if (scrollTop + windowHeight + 10 >= documentHeight) {
      setFireMoreLoadEvent((prev) => !prev);
    }
  };

  const checkScrollable = () => document.documentElement.scrollHeight > window.innerHeight;

  // 中のコンテンツが小さいとスクロールできず、無限ローディングさせることができないため、事前に+αで読んでおく。
  useEffect(() => {
    if (limit <= stockList.length && stockList.length < total && !isLoading && !isFastLoading.current) {
      if (!checkScrollable()) {
        (async() => {
          // 仕入先の場合は仕入先コードをfillするために取得
          const isSup = checkIsSupplierParam();
          if (isSup) {
            const supCd = getSupplierParam();
            await getMoreStockList(createGetListApiParams(), isSup, supCd);
          } else {
            await getMoreStockList(createGetListApiParams(), isSup);
          }
        })();
      }
    }
  }, [stockList, isLoading]);

  useEffect(() => {
    setTimeout(() => {
      const userType = localStorage.getItem("user_type");
      if (!(userType === "C0001" || userType === "C0005")) {
        setIsOpenStockSearch(false);
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: "本部ユーザーでログインしてください。再度ログインしてください。",
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      } else if (checkAccessTokenExpired()) {
        // 期限切れなら
        setIsOpenStockSearch(false);
        Notify?.setNotification?.({
          show: true,
          title: ERROR_MESSAGE.UNAUTHORIZED.TITLE,
          message: ERROR_MESSAGE.UNAUTHORIZED.MESSAGE,
        });
        Notify.onClose = () => {
          localStorage.clear();
          window.location.href = "/login";
        };
      }
    }, 0);
  }, []);
  useEffect(() => {
    // addEventListenerするとstateの値を変更しても内部の値は変わらないため、stateを変更させるだけにし、ここで処理を行う。
    (async () => {
      // 仕入先の場合は仕入先コードをfillするために取得
      const isSup = checkIsSupplierParam();
      if (isSup) {
        const supCd = getSupplierParam();
        await getMoreStockList(createGetListApiParams(), isSup, supCd);
      } else {
        await getMoreStockList(createGetListApiParams(), isSup);
      }
    })();
  }, [fireMoreLoadEvent]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    (async () => {
      // if (defaultSupplierStorehouseCd) {
      //   // 仕入先の場合は仕入先コードをfillするために取得
      //   const isSup = checkIsSupplierParam();
      //   if (isSup) {
      //     const supCd = getSupplierParam();
      //     await getStockList(createGetListApiParams(undefined, true), isSup, supCd);
      //   } else {
      //     await getStockList(createGetListApiParams(undefined, true), isSup);
      //   }
      // }
      setInit(true);
    })();
  }, []);
  useEffect(() => {
    setSupplierName(supplier.length ? supplier : storehouse);
  }, [supplier, storehouse]);
  useEffect(() => {
    // loadingの統一
    setIsLoading(isOptionsLoading || isStockLoading);
  }, [isOptionsLoading, isStockLoading]);

  return (
    <Box>
      <StockContainer
        stockTitle={`${title.length > 0 ? title : ""}在庫更新`}
        StockSearch={
          <StockSearchDialog
            open={isOpenStockSearch}
            onClose={() => setIsOpenStockSearch(false)}
            onSearch={onSearchClick}
            productCd={productCd}
            productName={productName}
            brand={brand}
            JANCd={JANCd}
            productNumber={productNumber}
            tyreWidth={tyreWidth}
            tyreRatio={tyreRatio}
            tyreRimDiam={tyreRimDiam}
            wheelWidth={wheelWidth}
            wheelPcd={wheelPcd}
            wheelHole={wheelHole}
            wheelRimDiam={wheelRimDiam}
            classification={classification}
            storehouseSupplier={storehouseSupplierWithBlank}
            storehouse={storehouse}
            supplier={supplier}
            classificationOptions={classificationOptions}
            tyreOptions={tyreOptions}
            wheelOptions={wheelOptions}
            storehouseOptions={storehouseOptions}
            // supplierOptions={supplierOptions}
            brandOptions={brandOptions}
            setProductCd={setProductCd}
            setProductName={setProductName}
            setBrand={setBrand}
            setJANCd={setJANCd}
            setProductNumber={setProductNumber}
            setTyreWidth={setTyreWidth}
            setTyreRatio={setTyreRatio}
            setTyreRimDiam={setTyreRimDiam}
            setWheelWidth={setWheelWidth}
            setWheelPcd={setWheelPcd}
            setWheelHole={setWheelHole}
            setWheelRimDiam={setWheelRimDiam}
            setClassification={setClassification}
            setCategory={setCategory}
            setStorehouseSupplier={setStorehouseSupplierWithBlank}
            setStorehouse={setStorehouse}
            setSupplier={setSupplier}
            isProductStockScreen
          />
        }
        StockTable={
          <StockTable
            searchLen={total}
            tableHeadElement={tableHeadElement}
            tableBodyElement={tableBodyElement}
            isAllLoaded={stockList.length >= total}
            isLoading={isMoreStockLoading}
            supplierCd={extractNoDupplicationSupplier(classificationOptions).some(so => so.supplierName === title) ? extractNoDupplicationSupplier(classificationOptions).filter(so => so.supplierName === title)[0].supplierCd : ''}
            isSupplier={checkIsSupplier()}
          />
        }
        setIsOpenStockSearch={setIsOpenStockSearch}
        isLoading={isLoading}
        onUpdate={() => onUpdateConfirm()}
        changedStockList={changedStockList}
        isReadonly={isReadonly}
        onChangeReadonlyScreen={onChangeReadonlyScreen}
        setIsOpenStockCsvUpload={setIsOpenStockCsvUpload}
        setIsOpenThresholdCsvUpload={setIsOpenThresholdCsvUpload}
        supplierCd={
          storehouseSupplier === storehouseSupplierOptions[1] &&
          extractNoDupplicationSupplier(classificationOptions).some(
            (so) => so.supplierName === title,
          )
            ? extractNoDupplicationSupplier(classificationOptions).filter(
                (so) => so.supplierName === title,
              )[0].supplierCd
            : ""
        }
        storehouseCd={
          storehouseSupplier === storehouseSupplierOptions[0] &&
          storehouseOptions.some((so) => so.name === supplierName)
            ? storehouseOptions.filter((so) => so.name === supplierName)[0].id
            : ""
        }
        csvDownloading={csvDownloading}
        onBack={handleBack}
        manualName="manual_product_update"
      />
      <StockUpdateConfirmDialog
        open={isOpenUpdateConfirmDialog}
        onClose={setIsOpenUpdateConfirmDialog}
        onUpdate={onUpdateAmount}
        changedStockList={changedStockList}
        isUpdateProcessing={isUpdateProcessing}
        isSupplier={checkIsSupplier()}
        selectHeaderRegionWarehouse={selectHeaderRegionWarehouse} // 近隣倉庫があればSelectbox要素が入る
        selectHeaderCenterWarehouse={selectHeaderCenterWarehouse} // センター倉庫があればSelectbox要素が入る
        bodyCenterWarehouse={bodyCenterWarehouse}
        bodyRegionWarehouse={bodyRegionWarehouse}
      />
      <CsvModals
        isOpenStockCsvUpload={isOpenStockCsvUpload}
        setIsOpenStockCsvUpload={setIsOpenStockCsvUpload}
        refresh={() => getStockListUpdate()}
        isOpenThresholdCsvUpload={isOpenThresholdCsvUpload}
        setIsOpenThresholdCsvUpload={setIsOpenThresholdCsvUpload}
        supplierCd={
          extractNoDupplicationSupplier(classificationOptions).some(
            (so) => so.supplierName === title,
          )
            ? extractNoDupplicationSupplier(classificationOptions).filter(
                (so) => so.supplierName === title,
              )[0].supplierCd
            : undefined
        }
        storehouseCd={
          storehouseOptions.some((so) => so.name === title)
            ? storehouseOptions.filter((so) => so.name === title)[0].id
            : undefined
        }
      />
      {isScrollTop &&
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: (theme) => theme.zIndex.speedDial,
          }}
        >
          <SpeedDial
            ariaLabel="Back to top"
            icon={<KeyboardArrowUpIcon />}
            onClick={scrollToTop}
            open             // 常に展開済みにしてアイコンだけ表示
            direction="up"          // 展開方向は上だがActionは不要
            FabProps={{ size: 'small' }}
            />
        </Box>
      }
      {/* 都道府県ダイアログ */}
      <PrefDialog open={isOpenStockPref} onClose={handlePrefStockClose} selectedPrefProduct={selectedPrefProduct} getPrefStock={getPrefStock} prefStockList={prefStockList} isLoading={isPrefLoading}/>
    </Box>
  );
}
